'use es6';

const THROTTLED_REQUEST_TIME = 1000;
export default class IframeModuleMarkupRequestManager {
  constructor({
    onMarkupReceived,
    onMarkupRequest
  }) {
    this.handleNewMarkupResponse = ({
      moduleName,
      moduleVersion,
      moduleHtml,
      cssToAdd
    }) => {
      if (cssToAdd && cssToAdd.length > 0) {
        const cssEndMeta = document.querySelector('meta[name="hs-head-css-end"]');
        cssToAdd.forEach(link => cssEndMeta.insertAdjacentHTML('beforebegin', link));
      }
      const tempDiv = document.createElement('div');
      tempDiv.insertAdjacentHTML('afterbegin', moduleHtml);
      const moduleDomNode = tempDiv.firstChild;
      this.onMarkupReceived(moduleName, moduleVersion, moduleDomNode);

      // If the AppModuleMarkupRequestManager had any issues getting markup from
      // the server, it handles catching the error and refreshes the iframe,
      //  blowing away this promise / cache state. If we've made it this far,
      // everything went well in the parent frame.
      if (this.requests[moduleName] && this.requests[moduleName].promises && this.requests[moduleName].promises[moduleVersion]) {
        this.requests[moduleName].promises[moduleVersion].resolve({
          moduleName,
          moduleVersion,
          isStaleRequest: false
        });
        delete this.requests[moduleName].promises[moduleVersion];
      }
    };
    this.handleRefreshRequiredResponse = ({
      moduleName,
      moduleVersion,
      moduleHasJs,
      moduleHasCss
    }) => {
      this.resolvePromise({
        moduleName,
        moduleVersion,
        isStaleRequest: false,
        moduleHasJs,
        moduleHasCss
      });
    };
    this.onMarkupReceived = onMarkupReceived;
    this.onMarkupRequest = onMarkupRequest;
    this.requests = {};
  }

  // This method:
  // - Returns a promise keyed by the module name and module version each time its called
  // - Resolves unresolved promises for a module request as "stale" if this method is called again
  //   before the previous promise was resolved. The code will ignore rendering changes in the DOM
  //   if the promise was resolved as "stale".
  // - Sets and keeps track of timers for each module request on a per module name basis.
  // - If enough time has past since the last time we dispatched a request to the parent frame for HTML,
  //   we dispatch immediately. Otherwise setTimeout the dispatch in a throttled manner.
  requestMarkup(moduleName, moduleVersion, options = {}) {
    if (!this.requests[moduleName]) {
      this.requests[moduleName] = {
        promises: {},
        timedDispatchId: null,
        lastRequestTime: null,
        nextVersionToRequest: null
      };
    }
    this.cancelUnfiredTimersAndResolvePromisesAsStale(moduleName);
    const now = Date.now();
    const lastRequestTime = this.requests[moduleName].lastRequestTime;
    const timeUntilNextAllowedRequest = lastRequestTime !== null ? THROTTLED_REQUEST_TIME - (now - lastRequestTime) : 0;
    const allowedToMakeAnotherRequest = timeUntilNextAllowedRequest <= 0;

    // If it's been long enough since the last request, make another one now
    if (allowedToMakeAnotherRequest) {
      this.dispatchRequestForMarkupNow(moduleName, moduleVersion, now, options);
    } else {
      this.dispatchRequestForMarkupLater(moduleName, moduleVersion, timeUntilNextAllowedRequest, options);
    }
    return this.createNewPromise(moduleName, moduleVersion);
  }
  dispatchRequestForMarkupNow(moduleName, moduleVersion, currentTime, options) {
    this.onMarkupRequest(moduleName, moduleVersion, options);
    this.requests[moduleName].lastRequestTime = currentTime;
  }
  dispatchRequestForMarkupLater(moduleName, moduleVersion, timeUntilNextAllowedRequest, options) {
    const timedDispatchId = setTimeout(() => {
      const now = Date.now();
      this.dispatchRequestForMarkupNow(moduleName, moduleVersion, now, options);
      // Remove the timedDispatchId now that this timer has fired
      delete this.requests[moduleName].timedDispatchId;
      delete this.requests[moduleName].nextVersionToRequest;
    }, timeUntilNextAllowedRequest);

    // Save reference timer id and the version being requested
    this.requests[moduleName].timedDispatchId = timedDispatchId;
    this.requests[moduleName].nextVersionToRequest = moduleVersion;
  }
  cancelUnfiredTimersAndResolvePromisesAsStale(moduleName) {
    const {
      timedDispatchId,
      nextVersionToRequest
    } = this.requests[moduleName];
    if (timedDispatchId) {
      clearTimeout(timedDispatchId);
      this.requests[moduleName].promises[nextVersionToRequest].resolve({
        moduleName,
        moduleVersion: nextVersionToRequest,
        isStaleRequest: true
      });
      delete this.requests[moduleName].timedDispatchId;
      delete this.requests[moduleName].nextVersionToRequest;
      delete this.requests[moduleName].promises[nextVersionToRequest];
    }
  }
  createNewPromise(moduleName, moduleVersion) {
    // Return a new promise. It with either be resolved as stale if more requests are made,
    // (and is ignored for rendering sake), or it's resolved when the markup gets
    // postMessage'd back down from the parent app.
    const promise = new Promise(resolve => {
      this.requests[moduleName].promises[moduleVersion] = {
        resolve
      };
    });
    return promise;
  }
  resolvePromise({
    moduleName,
    moduleVersion,
    isStaleRequest,
    moduleHasJs = false,
    moduleHasCss = false
  }) {
    if (this.requests[moduleName] && this.requests[moduleName].promises && this.requests[moduleName].promises[moduleVersion]) {
      this.requests[moduleName].promises[moduleVersion].resolve({
        moduleName,
        moduleVersion,
        isStaleRequest,
        moduleHasJs,
        moduleHasCss
      });
      delete this.requests[moduleName].promises[moduleVersion];
    }
  }
}